/**
 * Slip Detail Dialog
 * 
 * Dialog แสดงรายละเอียดสลิป
 */

import { 
  FileImage,
  Calendar,
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Hash,
  ExternalLink,
  Copy,
  User,
  CreditCard,
} from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { BankLogo } from '@/components/ui/bank-logo'
import { type SlipLog } from '@/domain/slip-log'
import { cn } from '@/lib/utils'

// ============================================
// Types
// ============================================

interface SlipDetailDialogProps {
  slip: SlipLog | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

// ============================================
// Helper Functions
// ============================================

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return '-'
  
  try {
    const date = new Date(dateStr)
    return date.toLocaleString('th-TH', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function formatCurrency(amount: number): string {
  return amount.toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

// ============================================
// Status Badge
// ============================================

function StatusBadge({ status, verificationStatus }: { 
  status?: string
  verificationStatus?: string 
}) {
  if (verificationStatus === 'success' || status === 'Approved') {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 gap-1 text-sm px-3 py-1">
        <CheckCircle2 className="w-4 h-4" />
        {verificationStatus === 'success' ? 'ตรวจสอบสำเร็จ' : 'อนุมัติแล้ว'}
      </Badge>
    )
  }
  
  if (verificationStatus === 'failed' || status === 'Rejected') {
    return (
      <Badge className="bg-rose-500/15 text-rose-600 border-rose-500/30 gap-1 text-sm px-3 py-1">
        <XCircle className="w-4 h-4" />
        {verificationStatus === 'failed' ? 'ตรวจสอบล้มเหลว' : 'ปฏิเสธ'}
      </Badge>
    )
  }
  
  if (verificationStatus === 'error') {
    return (
      <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 gap-1 text-sm px-3 py-1">
        <AlertCircle className="w-4 h-4" />
        เกิดข้อผิดพลาด
      </Badge>
    )
  }
  
  return (
    <Badge className="bg-zinc-500/15 text-zinc-600 border-zinc-500/30 gap-1 text-sm px-3 py-1">
      <Clock className="w-4 h-4" />
      รอดำเนินการ
    </Badge>
  )
}

// ============================================
// Info Row Component
// ============================================

function InfoRow({ 
  icon: Icon, 
  label, 
  value, 
  copyable = false,
  className 
}: { 
  icon: any
  label: string
  value: string | React.ReactNode
  copyable?: boolean
  className?: string
}) {
  const handleCopy = () => {
    if (typeof value === 'string') {
      navigator.clipboard.writeText(value)
    }
  }
  
  return (
    <div className={cn("flex items-start gap-3 py-2", className)}>
      <div className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className="flex items-center gap-2">
          <p className="font-medium truncate">{value || '-'}</p>
          {copyable && typeof value === 'string' && value && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleCopy}
            >
              <Copy className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================
// Bank Account Card
// ============================================

function BankAccountCard({ 
  title,
  bankId, 
  accountNumber, 
  name,
  isSource = false
}: { 
  title: string
  bankId?: string
  accountNumber?: string
  name?: string
  isSource?: boolean
}) {
  return (
    <div className={cn(
      "p-4 rounded-xl border-2",
      isSource 
        ? "border-blue-500/30 bg-blue-500/5" 
        : "border-emerald-500/30 bg-emerald-500/5"
    )}>
      <p className={cn(
        "text-xs font-medium mb-3",
        isSource ? "text-blue-600" : "text-emerald-600"
      )}>
        {title}
      </p>
      <div className="flex items-center gap-3">
        <BankLogo bankValue={bankId} size="lg" />
        <div className="flex-1 min-w-0">
          <p className="font-mono font-bold text-lg">{accountNumber || '-'}</p>
          {name && (
            <p className="text-sm text-muted-foreground truncate">{name}</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================
// Component
// ============================================

export function SlipDetailDialog({
  slip,
  open,
  onOpenChange
}: SlipDetailDialogProps) {
  if (!slip) return null

  // Get slip image URL - use runtime config for base URL
  const getImageUrl = () => {
    if (!slip.imagePath) return null

    // Cloud storage (Spaces/S3): already a full URL
    if (slip.imagePath.startsWith('http')) return slip.imagePath

    // Normalize old format to new location
    // Old format: "uploads/1771420494750.jpg" → "uploads/slips-old/1771420494750.jpg"
    // New format: "uploads/slips/slip-2026-02-18-xxx.jpg" → keep as-is
    let normalizedPath = slip.imagePath

    if (normalizedPath.startsWith('uploads/') && !normalizedPath.includes('slips/')) {
      // Old format detected - map to slips-old directory
      normalizedPath = normalizedPath.replace('uploads/', 'uploads/slips-old/')
    }

    // Try runtime config first (supports server switching without rebuild)
    const runtimeConfig = (window as any).APP_CONFIG
    if (runtimeConfig?.PARSE_V2_SERVER_URL) {
      // Remove /api/v1 suffix to get base URL
      const baseUrl = runtimeConfig.PARSE_V2_SERVER_URL.replace(/\/api\/v1$/, '')
      return `${baseUrl}/${normalizedPath}`
    }

    // Fallback to build-time env var
    const buildTimeUrl = import.meta.env.VITE_FRONT_API_URL || import.meta.env.VITE_PARSE_V2_SERVER_URL?.replace(/\/api\/v[12]$/, '') || ''
    return `${buildTimeUrl}/${normalizedPath}`
  }

  const imageUrl = getImageUrl()
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileImage className="w-5 h-5 text-blue-600" />
            รายละเอียดสลิป
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Amount & Status */}
          <div className="text-center py-4 bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-900 dark:to-zinc-800 rounded-xl">
            <p className="text-sm text-muted-foreground">ยอดโอน</p>
            <p className="text-4xl font-bold text-emerald-600 mt-1">
              ฿{formatCurrency(slip.amount)}
            </p>
            <div className="mt-3">
              <StatusBadge 
                status={slip.status} 
                verificationStatus={slip.verificationStatus} 
              />
            </div>
          </div>
          
          {/* Transfer Flow */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-center">
            <BankAccountCard
              title="บัญชีต้นทาง"
              bankId={slip.senderBankId}
              accountNumber={slip.senderBankAccount || slip.fromAccount}
              name={slip.senderName}
              isSource
            />
            
            <div className="hidden md:flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center">
                <ArrowRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </div>
            
            <BankAccountCard
              title="บัญชีปลายทาง"
              bankId={slip.receiverBankId}
              accountNumber={slip.receiverBankAccount || slip.toAccount}
              name={slip.receiverName}
            />
          </div>
          
          <Separator />
          
          {/* Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <InfoRow
              icon={Calendar}
              label="วันเวลาโอน"
              value={formatDateTime(slip.datetime || slip.date)}
            />
            
            <InfoRow
              icon={Hash}
              label="Transfer ID"
              value={slip.transfer_id || '-'}
              copyable
            />
            
            <InfoRow
              icon={User}
              label="สมาชิก"
              value={slip.memberName || slip.memberUsername || '-'}
            />
            
            <InfoRow
              icon={CreditCard}
              label="ช่องทางอัปโหลด"
              value={
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={cn(
                    "text-xs",
                    slip.uploadFrom === 'Panel' 
                      ? "bg-sky-500/10 text-sky-600 border-sky-500/30"
                      : "bg-amber-500/10 text-amber-600 border-amber-500/30"
                  )}>
                    {slip.uploadFrom || 'Member'}
                  </Badge>
                  {slip.provider && (
                    <Badge variant="outline" className="text-xs">
                      {slip.provider === 'easyslip' ? 'EasySlip' : 'Slip2Go'}
                    </Badge>
                  )}
                </div>
              }
            />
          </div>
          
          {/* Message */}
          {slip.returnedMessage && (
            <div className={cn(
              "p-4 rounded-xl border-2",
              slip.returnedMessage.includes('สำเร็จ') || slip.returnedMessage.includes('เรียบร้อย')
                ? "bg-emerald-500/5 border-emerald-500/30"
                : slip.returnedMessage.includes('ซ้ำ') || slip.returnedMessage.includes('ไม่')
                ? "bg-rose-500/5 border-rose-500/30"
                : "bg-amber-500/5 border-amber-500/30"
            )}>
              <p className="text-sm font-medium">ข้อความจากระบบ:</p>
              <p className={cn(
                "mt-1",
                slip.returnedMessage.includes('สำเร็จ') || slip.returnedMessage.includes('เรียบร้อย')
                  ? "text-emerald-600"
                  : slip.returnedMessage.includes('ซ้ำ') || slip.returnedMessage.includes('ไม่')
                  ? "text-rose-600"
                  : "text-amber-600"
              )}>
                {slip.returnedMessage}
              </p>
            </div>
          )}
          
          {/* Duplicate Info */}
          <div className="flex items-center justify-between p-4 bg-zinc-50 dark:bg-zinc-900 rounded-xl">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">สถานะซ้ำ:</span>
              {slip.slip_count === 1 && slip.slip_new ? (
                <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  ไม่ซ้ำ
                </Badge>
              ) : (
                <Badge className="bg-rose-500/15 text-rose-600 border-rose-500/30 gap-1">
                  <XCircle className="w-3 h-3" />
                  ซ้ำ ({slip.slip_count || 0} ครั้ง)
                </Badge>
              )}
            </div>
          </div>

          {/* Slip Image */}
          {imageUrl && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">รูปสลิป</p>
                <Button variant="ghost" size="sm" className="gap-2 h-8" asChild>
                  <a href={imageUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3" />
                    เปิดในหน้าใหม่
                  </a>
                </Button>
              </div>
              <div className="relative rounded-xl overflow-hidden border-2 border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-900">
                <img
                  src={imageUrl}
                  alt="รูปสลิป"
                  className="w-full h-auto max-h-[600px] object-contain"
                  loading="lazy"
                />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}









